import { Markdown } from "@/components/molecules/markdown";
import { UnsureNameMark } from "@/components/molecules/unsure-name-mark";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  CardTitle as DialogTitle,
} from "@/components/ui/dialog";
import type { ClientMessage } from "@/features/messages/types";
import { useDateFormat, useTranslation } from "@/features/utils/i18n";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useHasPermission } from "@/features/clients/hooks/use-permissions";
import { HandleMessageMenu } from "./handle-menu";
import { MessageTimeline } from "./message-timeline";

/**
 * One request, and what to do with it, over a list that is not the Messagerie
 * (a contact's file). The Messagerie itself shows the same button in its reading
 * pane; both are `HandleMessageMenu`.
 */
export const HandleMessageDialog = ({
  message,
  open,
  onOpenChange,
}: {
  message: ClientMessage;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useTranslation("messages");
  const { format } = useDateFormat();
  const { company: clientId } = useParams<{ company: string }>();
  const [resolveMenuOpen, setResolveMenuOpen] = useState(false);
  const canManage = useHasPermission("messages:manage");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl"
        // With the form's menu not modal, a click outside reaches both: it should
        // close the menu only, not the dialog and the answer being typed in it.
        onInteractOutside={(e) => {
          if (resolveMenuOpen) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>{message.subject}</DialogTitle>
          <DialogDescription>
            {message.customer_name && (
              <>
                {message.customer_name}
                {message.unsure_customer_name && <UnsureNameMark />}
                {" · "}
              </>
            )}
            {[message.phone_number, format(message.created_at || 0, "Pp")]
              .filter(Boolean)
              .join(" · ")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-4">
          <Markdown className="bg-muted/50 rounded-md p-3">
            {message.body}
          </Markdown>

          {message.session_id && (
            <Link
              to={`/${clientId}/calls/session/${message.session_id}`}
              className="text-primary block w-fit text-sm underline-offset-4 hover:underline"
            >
              {t("handle.openConversation")}
            </Link>
          )}

          {open && <MessageTimeline message={message} canManage={canManage} />}

          {open && message.status === "pending" && canManage && (
            <div className="flex justify-end">
              <HandleMessageMenu
                message={message}
                onDone={() => onOpenChange(false)}
                onOpenChange={setResolveMenuOpen}
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
